import { useLocation } from 'react-router';
import { useMemo } from 'react';
import type { ProgressStep } from '@/types';
import { preQuestionnaireStore } from '@/store/preQuestionnaireStore';

/**
 * Utility: Matches the current path against a dynamic template like `/products/:id/...`
 */
const isDynamicPath = (path: string, template: string) => {
  let regexPattern = template.replace(/:[a-zA-Z0-9_]+/g, '([^/]+)');

  // Optional ending for RDT product intro
  if (template.endsWith('/intro')) {
    regexPattern += '(?:/rdt)?';
  }

  // Allow optional `/pre_questionnaire` segment
  if (template.endsWith('/pre_questionnaire')) {
    regexPattern = regexPattern.replace('/pre_questionnaire', '(?:/pre_questionnaire)?');
  }

  // Match exact route with optional trailing slash
  const regex = new RegExp(`^${regexPattern}/?$`);
  return regex.test(path);
};

/**
 * Utility: Generates a path string for each step, depending on the section or ID
 */
const generateStepPath = (step: Omit<ProgressStep, 'path'>): string => {
  if (step.id === 'pre_questionnaire') {
    return '/products/:identifier/pre_questionnaire';
  }

  if (step.section === 'Product') {
    return `/products/:identifier/${step.id.replace('product-', '').replace('_', '-')}`;
  }

  return `/${step.id}`;
};

/**
 * Hook: Returns step configuration and progress state based on the current route
 */
export const useProgressSteps = (dynamicQuestionnairePaths: string[] = []) => {
  const { pathname } = useLocation();
  const { questions } = preQuestionnaireStore();
  const preQuestionnaireQuestionsLength = questions.length;

  /**
   * Step definition for the pre-questionnaire, rendered as its own progress segment
   */
  const preQuestionnaireStep = useMemo(
    () => ({
      id: 'pre_questionnaire',
      title: 'Product PreQuestionnaire',
      section: 'PreQuestionnaire',
    }),
    [],
  );

  /**
   * Main steps of the funnel, including dynamic medical questions if available
   */
  const mainSteps = useMemo<ProgressStep[]>(() => {
    const baseStepDefinitions: Omit<ProgressStep, 'path'>[] = [
      { id: 'product-intro', title: 'Product Intro', section: 'Product' },
      { id: 'dissolving-tablet', title: 'Product Intro', section: 'Product' },
      { id: 'variant-selection', title: 'Variant Selection', section: 'Product' },
      { id: 'quantity-selection', title: 'Quantity Selection', section: 'Product' },
      { id: 'carts', title: 'Cart', section: 'Checkout' },
    ];

    const baseSteps = baseStepDefinitions.map((step) => ({
      ...step,
      path: generateStepPath(step),
    }));

    // Build dynamic questionnaire steps (e.g., questions per user)
    const questionnaireSteps = dynamicQuestionnairePaths.map((path, index) => ({
      id: `questionnaire-${index + 1}`,
      title: `Question ${index + 1}`,
      path,
      section: 'Medical Questions',
    }));

    // Insert dynamic questionnaire steps before 'product-intro'
    const insertBeforeId = 'product-intro';
    const insertIndex = baseSteps.findIndex((step) => step.id === insertBeforeId);

    return questionnaireSteps.length > 0
      ? [...baseSteps.slice(0, insertIndex), ...questionnaireSteps, ...baseSteps.slice(insertIndex)]
      : baseSteps;
  }, [dynamicQuestionnairePaths]);

  /**
   * Conditionally return pre-questionnaire step if applicable
   */
  const preQuestionnaireSteps = useMemo(() => {
    if (preQuestionnaireQuestionsLength > 0) {
      return [
        {
          ...preQuestionnaireStep,
          path: generateStepPath(preQuestionnaireStep),
        },
      ];
    }
    return [];
  }, [preQuestionnaireQuestionsLength, preQuestionnaireStep]);

  /**
   * Decide which set of steps to use (pre-questionnaire or main flow)
   */
  const activeSteps = useMemo(
    () => [...preQuestionnaireSteps, ...mainSteps],
    [preQuestionnaireSteps, mainSteps],
  );

  /**
   * Find the current step index based on the current route
   */
  const computedActiveStepIndex = activeSteps.findIndex((step) =>
    isDynamicPath(pathname, step.path),
  );

  /**
   * Special handling to ensure `/signup` always returns step 0
   */
  const activeStepIndex = computedActiveStepIndex;

  /**
   * Calculate % progress through the current step flow
   */
  const progressPercentage = useMemo(() => {
    if (activeStepIndex <= 0 || activeSteps.length === 1) return 0;
    return (activeStepIndex / (activeSteps.length - 1)) * 100;
  }, [activeSteps, activeStepIndex]);

  /**
   * Which section (e.g., "Product", "Checkout") the current step belongs to
   */
  const currentSection = activeSteps[activeStepIndex]?.section || 'General';

  /**
   * Flag used to determine if we should show the progress bar
   */
  const showProgressBar = activeStepIndex !== -1;

  /**
   * Organize steps into grouped sections for display or structure
   */
  const groupedSections = useMemo(() => {
    return activeSteps.reduce<Record<string, ProgressStep[]>>((acc, step) => {
      const section = step.section ?? 'General';
      if (!acc[section]) acc[section] = [];
      acc[section].push(step);
      return acc;
    }, {});
  }, [activeSteps]);

  return {
    steps: activeSteps,
    groupedSections,
    currentStepIndex: activeStepIndex,
    totalSteps: activeSteps.length,
    progressPercentage,
    currentSection,
    showProgressBar,
  };
};
